import { useState, useEffect, useRef, useCallback } from 'react';
import { saveDraft, loadDraft, clearDraft, wasDraftExplicitlyCleared } from '../utils/draftStorage';

// ── useDraft ─────────────────────────────────────────────────────────
// Persists to localStorage, scoped by logged-in username (via ACTIVE_USERNAME
// / optional username arg). Saves SYNCHRONOUSLY on every setValue so a fast
// tab close / refresh cannot lose text waiting on a debounce timer.
export function useDraft(key, initial = '', username) {
  const [value, setValueState] = useState(() => (key ? (loadDraft(key, username) ?? initial) : initial));
  const [saved, setSaved] = useState(() => !!(key && loadDraft(key, username)));
  const valueRef = useRef(value);

  // key/username this render owns — updated during render so setValue can
  // persist immediately. Separate "flushOwned*" refs are snapped at effect
  // start so unmount cleanup never clears/saves under the *next* scope.
  const keyRef = useRef(key);
  const userRef = useRef(username);
  keyRef.current = key;
  userRef.current = username;

  const flushKeyRef = useRef(key);
  const flushUserRef = useRef(username);

  const persist = useCallback((k, v, user) => {
    if (!k) return;
    if (v) {
      saveDraft(k, v, user);
      setSaved(true);
    } else {
      clearDraft(k, user);
      setSaved(false);
    }
  }, []);

  const setValue = useCallback((next) => {
    setValueState((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      valueRef.current = resolved;
      persist(keyRef.current, resolved, userRef.current);
      return resolved;
    });
  }, [persist]);

  // Reload when draft key or account scope changes.
  useEffect(() => {
    flushKeyRef.current = key;
    flushUserRef.current = username;

    if (!key) {
      valueRef.current = initial;
      setValueState(initial);
      setSaved(false);
      return;
    }
    const restored = loadDraft(key, username);
    const next = restored ?? initial;
    valueRef.current = next;
    setValueState(next);
    setSaved(!!restored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, username]);

  // Flush on unmount using the scope THIS effect instance owned.
  useEffect(() => () => {
    const k = flushKeyRef.current;
    if (!k) return;
    if (wasDraftExplicitlyCleared(k, flushUserRef.current)) return;
    const v = valueRef.current;
    if (v) saveDraft(k, v, flushUserRef.current);
  }, [key, username]);

  const clear = useCallback(() => {
    valueRef.current = '';
    setValueState('');
    setSaved(false);
    if (key) clearDraft(key, username);
  }, [key, username]);

  return [value, setValue, clear, saved];
}

// ── usePolling ────────────────────────────────────────────────────────
export function usePolling(fn, intervalMs = 15000, enabled = true) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) return;
    fnRef.current();
    const id = setInterval(() => fnRef.current(), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs, enabled]);
}

// ── useDebounce ───────────────────────────────────────────────────────
export function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ── useAsync ──────────────────────────────────────────────────────────
export function useAsync(asyncFn, deps = []) {
  const [state, setState] = useState({ data: null, loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    asyncFn()
      .then((res) => {
        if (!cancelled) setState({ data: res.data, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ data: null, loading: false, error: err.message });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}
